// File: src/pages/NfpDashboard.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { nfpApi } from '../services/nfpApi';
import { apiFetch, setAuthToken, setOrgContext } from '../api/client';
import tealBack from '../Images/teal_dback.png';
import rypilLo from '../Images/rypilLo.png';
import { renderRypilText } from '../lib/rypilText';

const PEACH = '#EBA477';
const MIN_LEVELS_PER_SEGMENT = 3;
const DEFAULT_MILESTONE_LEVELS = {
  individual: ['20', '50', '100'],
  community: ['500', '1000', '2500'],
};
//const MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // 5 MB (may be used elsewhere later)

const centsFromDollars = (value) => {
  if (value == null || value === '') return 0;
  const num = Number(value);
  if (Number.isNaN(num)) return 0;
  return Math.max(0, Math.round(num * 100));
};

const dollarsFromCents = (value) =>
  typeof value === 'number' && !Number.isNaN(value) && value > 0
    ? (value / 100).toString()
    : '';

const randomRowId = (segment) =>
  `${segment}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const ensureMilestoneRows = (list, segment) => {
  const defaults = DEFAULT_MILESTONE_LEVELS[segment] || [];
  const rows = list.map((row, idx) => ({
    id: row.id || row._id || `${segment}-${idx}`,
    segment,
    amount: row.amount ?? dollarsFromCents(row.thresholdCents),
    description: row.description || '',
  }));
  while (rows.length < MIN_LEVELS_PER_SEGMENT) {
    const idx = rows.length;
    rows.push({
      id: randomRowId(segment),
      segment,
      amount: defaults[idx] ?? '',
      description: '',
    });
  }
  return rows;
};

// Merges checklist updates without accidentally downgrading a previously completed item.
// Set allowDowngrade=true to fully trust server-observed state (e.g., initial load).
const mergeChecklist = (setState, updates, { allowDowngrade = false } = {}) => {
  if (!updates) return;
  setState((prev) => {
    const next = { ...prev };
    Object.entries(updates).forEach(([key, value]) => {
      const bool = !!value;
      if (allowDowngrade || bool || typeof prev[key] === 'undefined') {
        next[key] = bool;
      }
    });
    return next;
  });
};

const normalizeReasons = (value) => (Array.isArray(value) ? value : []);

const deriveChecklistFromReasons = (reasonsRaw, fallback = {}) => {
  const reasons = new Set(normalizeReasons(reasonsRaw));
  const has = (key) => reasons.has(key);
  return {
    logo: has('missing_logo') ? false : !!fallback.logo,
    images: has('missing_images_min3') || has('missing_images') ? false : !!fallback.images,
    whySupport: has('missing_why_support') ? false : !!fallback.whySupport,
    mission: has('missing_mission') ? false : !!fallback.mission,
    areas: has('missing_areas') ? false : !!fallback.areas,
    termsSigned: has('signatory_not_signed') ? false : !!fallback.termsSigned,
    proofVerified: has('proof_unsubmitted') || has('proof_pending') || has('proof_rejected')
      ? false
      : !!fallback.proofVerified,
    moderationPassed: has('moderation_pending') || has('moderation_rejected')
      ? false
      : !!fallback.moderationPassed,
  };
};

const deriveListingStatus = (visibility) => {
  const state = visibility?.state || 'hidden_pending';
  const reasons = normalizeReasons(visibility?.reasons);
  const reviewReasons = new Set(['proof_pending', 'moderation_pending', 'inactive']);
  const hasReviewOnly =
    reasons.length > 0 &&
    reasons.every((r) => reviewReasons.has(r));

  if (state === 'blocked_admin') {
    return { label: 'App listing: Blocked', color: '#F87171' };
  }
  if (state === 'suspended') {
    return { label: 'App listing: Suspended', color: '#FBBF24' };
  }
  if (state === 'visible') {
    return { label: 'App listing: Live', color: '#34D399' };
  }
  if (hasReviewOnly) {
    return { label: 'App listing: In review', color: '#FBBF24' };
  }
  return { label: 'App listing: Incomplete', color: '#F87171' };
};

const derivePaymentsStatus = (stripeStatus, payoutReadiness, isStripeConnected) => {
  if (stripeStatus !== 'active') {
    if (stripeStatus === 'pending') {
      return { label: 'Payments: Connected (pending)', color: '#FBBF24' };
    }
    if (isStripeConnected) {
      return { label: 'Payments: Connected (not ready)', color: '#FBBF24' };
    }
    return { label: 'Payments: Stripe not connected', color: '#F87171' };
  }
  if (payoutReadiness?.payoutReady) {
    return { label: 'Payments: Ready', color: '#34D399' };
  }
  return { label: 'Payments: Connected (not ready)', color: '#FBBF24' };
};

const inferVerificationProofType = (cause) => {
  const proofType = cause?.proof?.type || '';
  if (proofType) return proofType;
  const proof = cause?.proof || {};
  if (proof.letterOfDeterminationUrl) return 'lod';
  if (proof.operatingUnderGenDocUrl || proof.chapterAuthorizationUrl) return 'operating_under_gen';
  if (proof.governmentUnitDocUrl) return 'government_unit';
  if (proof.otherSupportingDocUrl) return 'other_supporting';
  return '';
};

const getVerificationDocKeys = (cause) => {
  const recognitionType = cause?.statusRecognition?.type || '';
  const coveredByParent = cause?.statusRecognition?.isCoveredByParentGroupExemption || '';
  switch (recognitionType) {
    case 'irs_determination_letter':
      return ['letterOfDeterminationUrl'];
    case 'parent_group_exemption':
      return ['operatingUnderGenDocUrl', 'chapterAuthorizationUrl'];
    case 'religious_no_separate_letter':
      return coveredByParent === 'yes'
        ? ['operatingUnderGenDocUrl', 'chapterAuthorizationUrl']
        : ['otherSupportingDocUrl'];
    case 'government_unit_public_entity':
      return ['governmentUnitDocUrl'];
    case 'other_not_sure':
      return ['otherSupportingDocUrl'];
    default:
      switch (inferVerificationProofType(cause)) {
        case 'lod':
          return ['letterOfDeterminationUrl'];
        case 'operating_under_gen':
          return ['operatingUnderGenDocUrl', 'chapterAuthorizationUrl'];
        case 'government_unit':
          return ['governmentUnitDocUrl'];
        case 'other_supporting':
          return ['otherSupportingDocUrl'];
        default:
          return [];
      }
  }
};

const deriveVerificationStatus = (cause) => {
  const proofStatus = cause?.proof?.status || cause?.proofVerificationStatus || 'unsubmitted';
  if (proofStatus === 'verified') {
    return { label: 'Verified', color: '#34D399', cta: 'View status' };
  }
  if (proofStatus === 'rejected') {
    return { label: 'Needs attention', color: '#F87171', cta: 'Resolve issues' };
  }
  if (proofStatus === 'pending_review') {
    return { label: 'Submitted for review', color: '#FBBF24', cta: 'View status' };
  }

  const requiredKeys = getVerificationDocKeys(cause);
  const hasAnyUpload = requiredKeys.some((key) => Boolean(cause?.proof?.[key]));
  return hasAnyUpload
    ? { label: 'In progress', color: '#FBBF24', cta: 'Continue verification' }
    : { label: 'Not started', color: '#F87171', cta: 'Start verification' };
};

const normalizeMilestoneRows = (value = []) => {
  const source = Array.isArray(value) ? value : [];
  const grouped = { individual: [], community: [] };
  source.forEach((row, idx) => {
    const segment = row.segment === 'community' ? 'community' : 'individual';
    grouped[segment].push({
      id: row._id || `${segment}-${idx}`,
      segment,
      amount: dollarsFromCents(row.thresholdCents),
      description: row.description || '',
    });
  });
  return {
    individual: ensureMilestoneRows(grouped.individual, 'individual'),
    community: ensureMilestoneRows(grouped.community, 'community'),
  };
};

// Best-effort extraction of impact/areas served fields from the cause model
function extractImpactAreas(cause) {
  if (!cause) return [];
  const raw = [];

  const addValue = (val) => {
    if (!val) return;
    if (Array.isArray(val)) {
      val.forEach(addValue);
      return;
    }
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (trimmed) raw.push(trimmed);
      return;
    }
    if (typeof val === 'object') {
      if (val.label) return addValue(val.label);
      if (val.name) return addValue(val.name);
      if (val.title) return addValue(val.title);
    }
  };

  addValue(cause.impactAreas);
  addValue(cause.areasServed);
  addValue(cause.serviceAreas);
  addValue(cause.regions);
  addValue(cause.geographies);
  addValue(cause.consumerProfile?.impactAreas);

  const seen = new Set();
  return raw.filter((area) => {
    const key = area.toLowerCase();
    if (!area || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function NfpDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [cause, setCause] = useState(null);
  const [stripeStatus, setStripeStatus] = useState('not_connected');
  const [visibility, setVisibility] = useState(null);
  const [payoutReadiness, setPayoutReadiness] = useState(null);
  const [isStripeConnected, setIsStripeConnected] = useState(false);
  const [transfersStatus, setTransfersStatus] = useState('inactive');
  const [stripeRefreshBusy, setStripeRefreshBusy] = useState(false);

  // Contributions summary (read-only)
  const [payments, setPayments] = useState({
    nextPayoutDate: null,
    lastPayoutDate: null,
    lastPayoutAmountCents: 0,
    totalPayoutsCents: 0,
    paidOutCents: 0,
    lifetimeImpactCents: 0,
    availableForNextPayoutCents: 0,
    expectedNextPayoutCents: 0,
    inHoldCents: 0,
    pendingBalanceCents: 0,
    cadence: 'monthly',
    minDisbursementCents: 5000,
    nextScheduledPayoutDate: null,
  });

  // Supporters, locations, signatory
  const [supporterCount, setSupporterCount] = useState(0);
  const [locations, setLocations] = useState([]);
  const [signatory, setSignatory] = useState(null);

  const [checklist, setChecklist] = useState({
    logo: false,
    images: false,
    whySupport: false,
    mission: false,
    areas: false,
    termsSigned: false,
    proofVerified: false,
    moderationPassed: false,
  });

  // REMOVED: const [assets, setAssets] = useState({ logoUrl: '', images: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setErr('');
        setLoading(true);
        const resp = await nfpApi.getDashboard();
        if (cancelled) return;
        if (!resp?.cause) {
          const mine = await nfpApi.listMyRegistrations().catch(() => null);
          if (cancelled) return;
          const registrations = Array.isArray(mine?.registrations) ? mine.registrations : [];
          const hasOpenRegistration = registrations.some((reg) =>
            ['draft', 'needs_action', 'submitted', 'under_review'].includes(reg?.status)
          );
          if (hasOpenRegistration) {
            navigate('/nfp/register', { replace: true });
            return;
          }
          setErr('No nonprofit dashboard is available for this account yet.');
          return;
        }

        const c = resp?.cause || null;
        const s = resp?.stripeStatus || 'not_connected';

        // payout summary
        const p = resp?.payoutSummary || resp?.payoutSettings || {};
        const cl = resp?.profileChecklist || {};
        const vis = resp?.visibility || c?.visibility || null;
        const readiness = resp?.payoutReadiness || null;
        // REMOVED: const a = resp?.assets || {};

        setCause(c);
        const connected = !!c?.stripeConnect?.connectedAccountId;
        const transfers = c?.stripeConnect?.capabilities?.transfers || 'inactive';

        setIsStripeConnected(connected);
        setTransfersStatus(transfers);
        setStripeStatus(s);
        setVisibility(vis);
        setPayoutReadiness(readiness);

        setPayments({
          nextPayoutDate: p.nextPayoutDate || null,
          lastPayoutDate: p.lastPayoutDate || null,
          lastPayoutAmountCents:
            typeof p.lastPayoutAmountCents === 'number'
              ? p.lastPayoutAmountCents
              : typeof p.lastPayoutCents === 'number'
              ? p.lastPayoutCents
              : 0,
          totalPayoutsCents:
            typeof p.totalPayoutsCents === 'number'
              ? p.totalPayoutsCents
              : typeof p.totalPaidOutCents === 'number'
              ? p.totalPaidOutCents
              : 0,
          paidOutCents:
            typeof p.paidOutCents === 'number'
              ? p.paidOutCents
              : typeof p.totalPaidOutCents === 'number'
              ? p.totalPaidOutCents
              : typeof p.totalPayoutsCents === 'number'
              ? p.totalPayoutsCents
              : 0,
          lifetimeImpactCents:
            typeof p.lifetimeImpactCents === 'number'
              ? p.lifetimeImpactCents
              : typeof p.totalReceivedCents === 'number'
              ? p.totalReceivedCents
              : 0,
          availableForNextPayoutCents:
            typeof p.availableForNextPayoutCents === 'number'
              ? p.availableForNextPayoutCents
              : 0,
          expectedNextPayoutCents:
            typeof p.expectedNextPayoutCents === 'number'
              ? p.expectedNextPayoutCents
              : 0,
          inHoldCents:
            typeof p.inHoldCents === 'number'
              ? p.inHoldCents
              : 0,
          pendingBalanceCents:
            typeof p.pendingBalanceCents === 'number'
              ? p.pendingBalanceCents
              : typeof p.balanceCents === 'number'
              ? p.balanceCents
              : 0,
          cadence: p.cadence || 'monthly',
          minDisbursementCents:
            typeof p.minDisbursementCents === 'number' ? p.minDisbursementCents : 5000,
          nextScheduledPayoutDate: p.nextScheduledPayoutDate || null,
        });

        const derivedChecklist = deriveChecklistFromReasons(vis?.reasons, {
          logo: !!cl.logo,
          images: !!cl.images,
          whySupport: !!cl.whySupport,
          mission: !!cl.mission,
          areas: !!cl.areas,
          termsSigned: !!cl.termsSigned,
          proofVerified: !!cl.proofVerified,
          moderationPassed: !!cl.moderationPassed,
        });
        mergeChecklist(setChecklist, derivedChecklist, { allowDowngrade: true });

        // REMOVED:
        // setAssets({
        //   logoUrl: a.logoUrl || '',
        //   images: Array.isArray(a.images) ? a.images : [],
        // });

        setSupporterCount(
          typeof resp?.supporterCount === 'number'
            ? resp.supporterCount
            : typeof resp?.metrics?.supporterCount === 'number'
            ? resp.metrics.supporterCount
            : 0
        );

        // Try a few common shapes for locations
        const rawLocs =
          resp?.locations ||
          resp?.org?.locations ||
          resp?.organization?.locations ||
          resp?.cause?.locations ||
          [];
        setLocations(Array.isArray(rawLocs) ? rawLocs : []);

        const sigSource =
          resp?.cause?.agreements?.signatoryAcceptance ||
          resp?.agreements?.signatoryAcceptance ||
          resp?.authorizedSignatory ||
          resp?.signatory ||
          resp?.org?.authorizedSignatory ||
          resp?.organization?.authorizedSignatory ||
          null;
        const sig =
          sigSource?.signer || sigSource
            ? {
                ...(sigSource?.signer || sigSource),
                acceptedAt: sigSource?.acceptedAt || sigSource?.signedAt,
                termsVersion: sigSource?.termsVersion,
              }
            : null;
        setSignatory(sig || null);
      } catch (e) {
        if (!cancelled) setErr(e?.message || 'Failed to load dashboard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const headerSubtitle = '';

  const handleLogout = async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch {}
    try {
      setAuthToken(null);
      localStorage.removeItem('user');
      localStorage.removeItem('activeAccountId');
    } catch {}
    setOrgContext(null);
    navigate('/login');
  };

  const handleMarkHq = async (locationId) => {
    try {
      const target = locationId != null ? locationId.toString() : '';
      const resp = await nfpApi.setHeadquarters?.(target);
      const nextLocations = Array.isArray(resp?.locations)
        ? resp.locations
        : Array.isArray(resp?.cause?.locations)
        ? resp.cause.locations
        : null;
      if (nextLocations) {
        setLocations(nextLocations);
      } else {
        setLocations((prev) =>
          prev.map((loc) => ({
            ...loc,
            isHq: (loc.id ?? loc._id)?.toString() === target,
          }))
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to mark HQ', e);
    }
  };

  const impactAreas = extractImpactAreas(cause);
  const listingStatus = deriveListingStatus(visibility);
  const paymentsStatus = derivePaymentsStatus(stripeStatus, payoutReadiness, isStripeConnected);

  return (
    <div style={styles.fullscreen}>
      <div style={styles.dashboardMain}>
        <div style={styles.dashboardInner}>
          <div style={styles.headerRow}>
            <div>
              <h1 style={styles.headerTitle}>{cause?.name || 'Your Organization'}</h1>
              {headerSubtitle ? <div style={styles.headerSub}>{headerSubtitle}</div> : null}
            </div>
            <div style={styles.headerRight}>
              <StatusChip label={paymentsStatus.label} color={paymentsStatus.color} />
              <StatusChip label={listingStatus.label} color={listingStatus.color} />
              <button
                type="button"
                style={styles.manageProfileBtn}
                onClick={() => navigate('/nfp/profile')}
              >
                Manage profile
              </button>
              <button type="button" style={styles.logoutBtn} onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>

          {err && <div style={styles.errorCard}>{err}</div>}

          {loading ? (
            <div style={styles.loadingBlock}>
              <div style={styles.spinner} />
              <p>Loading dashboard…</p>
            </div>
          ) : (
            <>
              <div style={styles.columns}>
                {/* LEFT: Status, supporters */}
                <div style={styles.column}>
                  <ProfileChecklistCard
                    checklist={checklist}
                    onRefresh={async () => {
                      const resp = await nfpApi.getChecklist();
                      const nextVis = resp?.visibility || visibility;
                      const nextChecklist = deriveChecklistFromReasons(
                        nextVis?.reasons,
                        resp?.profileChecklist || {}
                      );
                      setVisibility(nextVis);
                      mergeChecklist(setChecklist, nextChecklist, { allowDowngrade: false });
                    }}
                  />

                  <VerificationDocumentsCard
                    cause={cause}
                    onOpen={() => navigate('/nfp/verification')}
                  />
                </div>

                {/* RIGHT: Contributions + Stripe & Signatory row + Profile summary */}
                <div style={styles.column}>
                  <ContributionsCard payments={payments} />

                  <div style={styles.inlineCardsRow}>
                    <StripeCard
                      stripeStatus={stripeStatus}
                      isStripeConnected={isStripeConnected}
                      transfersStatus={transfersStatus}
                      refreshBusy={stripeRefreshBusy}
                      onConnect={async () => {
                        const { url } = await nfpApi.beginStripeConnect();
                        if (url) window.location.href = url;
                      }}
                      onRefresh={async () => {
                        try {
                          setErr('');
                          setStripeRefreshBusy(true);
                          const refreshed = await nfpApi.refreshStripeStatus();
                          if (typeof refreshed?.stripeStatus === 'string') {
                            setStripeStatus(refreshed.stripeStatus);
                          }
                          const transfers = refreshed?.capabilities?.transfers;
                          if (typeof transfers === 'string') {
                            setTransfersStatus(transfers);
                          }
                          if (typeof refreshed?.connectedAccountId === 'string') {
                            setIsStripeConnected(!!refreshed.connectedAccountId);
                          }
                        } catch (e) {
                          setErr(e?.message || 'Failed to refresh Stripe status');
                        } finally {
                          setStripeRefreshBusy(false);
                        }
                      }}
                    />
                    <AuthorizedSignatoryCard signatory={signatory} />
                  </div>

                  <SupportersCard supporterCount={supporterCount} />

                </div>
              </div>

              {/* LOCATIONS + AREAS: split row */}
              <div style={styles.bottomRowSplit}>
                <LocationsCard locations={locations} onMarkHq={handleMarkHq} />
                <ImpactAreasCard areas={impactAreas} />
              </div>

              {/* IMPACT MILESTONES: full-width below */}
              <div style={styles.bottomRow}>
                <ImpactMilestonesCard
                  value={cause?.impactMilestones || []}
                  onSave={async (next) => {
                    const resp = await nfpApi.saveImpactMilestones(next);
                    if (resp?.cause) {
                      setCause(resp.cause);
                    }
                    if (resp?.profileChecklist) {
                      setChecklist((prev) => ({ ...prev, ...resp.profileChecklist }));
                    }
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <img src={rypilLo} alt="rypil mark" style={styles.badge} />
    </div>
  );
}

/** ============== Small, focused components ============== */

function StatusChip({ label, color }) {
  return (
    <span style={{ ...styles.statusChip, borderColor: color, color }}>
      <span style={{ ...styles.statusDot, background: color }} />
      {label}
    </span>
  );
}

function Card({ title, right, children, subtitle, style }) {
  return (
    <section style={{ ...styles.card, ...(style || {}) }}>
      <div style={styles.cardHeader}>
        <div>
          <div style={styles.cardTitle}>{title}</div>
          {subtitle && <div style={styles.cardSubtitle}>{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function StripeCard({ stripeStatus, isStripeConnected, transfersStatus, refreshBusy, onConnect, onRefresh }) {
  const transfersActive = transfersStatus === 'active';

  return (
    <Card title="Connect with Stripe" subtitle="Enable transfers to your bank account">
      <p style={styles.bodyText}>
        {transfersActive
          ? renderRypilText('Your Stripe account is connected and active. You can receive payouts from rypil.')
          : isStripeConnected
          ? 'Your Stripe account is connected, but transfers are not enabled yet. Please enable transfers in your Stripe account.'
          : stripeStatus === 'pending'
          ? 'Stripe onboarding is in progress. Complete verification in the Stripe dashboard.'
          : 'Connect your Stripe Standard account to receive payouts.'}
      </p>

      <div style={styles.stripeCardActions}>
        {!isStripeConnected && (
          <button style={styles.primaryBtn} onClick={onConnect}>
            Connect
          </button>
        )}
        {isStripeConnected && (
          <button style={styles.secondaryBtn} onClick={onRefresh} disabled={refreshBusy}>
            {refreshBusy ? 'Refreshing…' : 'Refresh Stripe status'}
          </button>
        )}
      </div>
    </Card>
  );
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

function fmtAmount(cents) {
  const val = typeof cents === 'number' && cents > 0 ? cents / 100 : 0;
  return `$${val.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function ContributionsCard({ payments }) {
  const {
    nextPayoutDate,
    nextScheduledPayoutDate,
    lastPayoutDate,
    lastPayoutAmountCents,
    totalPayoutsCents,
    paidOutCents,
    lifetimeImpactCents,
    availableForNextPayoutCents,
    expectedNextPayoutCents,
    inHoldCents,
    //pendingBalanceCents,
    cadence,
    minDisbursementCents,
  } = payments || {};

  const cadenceLabel =
    cadence === 'quarterly' ? 'Quarterly' : cadence === 'weekly' ? 'Weekly' : 'Monthly';

  const availableCents = typeof availableForNextPayoutCents === 'number' ? availableForNextPayoutCents : 0;
  const expectedCents = typeof expectedNextPayoutCents === 'number' ? expectedNextPayoutCents : 0;
  const minCents = typeof minDisbursementCents === 'number' ? minDisbursementCents : 0;
  const isBelowMin = availableCents >= 0 && availableCents < minCents;
  const isAdjustment = availableCents < 0;
  const expectedLabel = isAdjustment
    ? `Adjustment pending: ${fmtAmount(availableCents)}`
    : isBelowMin
    ? `Eligible (below ${fmtAmount(minCents)} minimum)`
    : `Expected: ${fmtAmount(expectedCents)}`;

  return (
    <Card
      title="Contributions"
      subtitle={renderRypilText('rypil contributions flowing to your organization')}
      right={
        <span style={styles.cardSubtitle}>
          Cadence: {cadenceLabel} • Min: {fmtAmount(minDisbursementCents)}
        </span>
      }
    >
      <div style={styles.contributionsTop}>
        <div style={styles.metricSlotLeft}>
          <div style={styles.metricLabel}>{renderRypilText('rypil payouts to date')}</div>
          <div style={styles.metricBig}>{fmtAmount(paidOutCents || totalPayoutsCents)}</div>
        </div>
        <div style={styles.metricSlotRight}>
          <div style={styles.metricLabel}>{renderRypilText('rypil Lifetime Impact')}</div>
          <div style={styles.metricBig}>{fmtAmount(lifetimeImpactCents)}</div>
        </div>
      </div>

      <div style={styles.inputGrid}>
        <div>
          <label style={styles.label}>Available for next payout</label>
          <div style={styles.metricValue}>{fmtAmount(availableCents)}</div>
          <div style={styles.subLabel}>{expectedLabel}</div>
        </div>
        <div>
          <label style={styles.label}>
            In hold <span style={styles.holdNote}>(not yet eligible)</span>
          </label>
          <div style={styles.metricValue}>{fmtAmount(inHoldCents)}</div>
        </div>
        <div>
          <label style={styles.label}>Next payout</label>
          <div style={styles.metricValue}>{fmtDate(nextPayoutDate)}</div>
          <div style={styles.subLabel}>
            Scheduled: {fmtDate(nextScheduledPayoutDate)}
          </div>
        </div>
        <div>
          <label style={styles.label}>Last payout</label>
          <div style={styles.metricValue}>
            {fmtDate(lastPayoutDate)} • {fmtAmount(lastPayoutAmountCents)}
          </div>
        </div>
      </div>
    </Card>
  );
}

function ProfileChecklistCard({ checklist, onRefresh }) {
  const items = [
    { key: 'logo', label: 'Logo uploaded' },
    { key: 'images', label: '3–5 images uploaded' },
    { key: 'whySupport', label: '“Why you should support” filled' },
    { key: 'mission', label: 'Mission category selected' },
    { key: 'areas', label: 'Areas served added' },
    { key: 'termsSigned', label: 'Master Terms signed' },
    { key: 'proofVerified', label: 'Proof verified' },
    { key: 'moderationPassed', label: 'Moderation passed' },
  ];
  return (
    <Card
      title="Profile completeness"
      subtitle="Complete these items to go live in the app"
      right={
        <button style={styles.linkBtn} onClick={onRefresh}>
          Refresh
        </button>
      }
    >
      <ul style={styles.checklist}>
        {items.map((item) => (
          <li key={item.key} style={styles.checklistItem}>
            <span
              style={{
                ...styles.checkDot,
                background: checklist[item.key]
                  ? '#34D399'
                  : 'rgba(255,255,255,0.15)',
              }}
            />
            <span style={{ opacity: checklist[item.key] ? 0.95 : 0.6 }}>{item.label}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function VerificationDocumentsCard({ cause, onOpen }) {
  const status = deriveVerificationStatus(cause);
  return (
    <Card
      title="Verification & Documents"
      subtitle="Manage documents used to verify your organization."
      right={<StatusChip label={status.label} color={status.color} />}
    >
      <p style={styles.bodyText}>
        Track verification review and app listing readiness here. Stripe setup stays separate.
      </p>
      <div style={styles.cardActionsRow}>
        <button type="button" style={styles.primaryBtn} onClick={onOpen}>
          {status.cta}
        </button>
      </div>
    </Card>
  );
}

function SupportersCard({ supporterCount }) {
  const formatted = (supporterCount || 0).toLocaleString();
  return (
    <Card title={renderRypilText('Supporters on rypil')} subtitle="People who’ve chosen you as their cause">
      <div style={styles.supportersRow}>
        <div style={styles.supporterNumber}>{formatted}</div>
        <div style={styles.supporterCopy}>
          As more supporters join, your visibility and impact through everyday purchases will
          grow.
        </div>
      </div>
    </Card>
  );
}

function formatLocationAddress(loc) {
  if (!loc) return 'Address not set';
  if (loc.addressText) return loc.addressText;
  const addr = loc.address || {};
  const line1 = loc.address1 || loc.line1 || addr.street || '';
  const line2 = loc.address2 || loc.line2 || '';
  const city = loc.city || addr.city || '';
  const state = loc.state || loc.region || addr.state || '';
  const postal = loc.postalCode || loc.zip || loc.postcode || addr.zip || '';
  const parts = [];
  if (line1) parts.push(line1);
  if (line2) parts.push(line2);
  const cityState = [city, state].filter(Boolean).join(', ');
  const cityLine = [cityState, postal].filter(Boolean).join(' ');
  if (cityLine) parts.push(cityLine);
  return parts.join('\n') || 'Address not set';
}

const formatWebsite = (value) => {
  if (!value) return null;
  try {
    const url = new URL(value.startsWith('http') ? value : `https://${value}`);
    return url.hostname.replace(/^www\./, '');
  } catch {
    return value;
  }
};

function LocationsCard({ locations, onMarkHq }) {
  const list = Array.isArray(locations) ? locations : [];
  const countLabel =
    list.length === 0
      ? renderRypilText('No locations registered with rypil yet')
      : list.length === 1
      ? renderRypilText('1 location you registered with rypil')
      : renderRypilText(`${list.length} locations you registered with rypil`);

  return (
    <Card title="Locations" subtitle={countLabel}>
      {list.length === 0 ? (
        <div style={styles.mutedText}>Locations from registration will appear here.</div>
      ) : (
        <div style={styles.locationList}>
          {list.map((loc, idx) => {
            const rawId = loc.id ?? loc._id ?? loc.placeId ?? idx;
            const id = rawId != null ? rawId.toString() : `${idx}`;
            const isHq = !!(loc.isHq || loc.isHeadquarters);
            const addressText = formatLocationAddress(loc);
            const phone = loc.phone || loc.phoneNumber || loc.contactPhone;
            const website = loc.website || loc.url;
            const canMark = !!id && !isHq;
            return (
              <div key={id} style={styles.locationRow}>
                <div style={styles.locationMain}>
                  <div style={styles.locationNameRow}>
                    <div style={styles.locationName}>
                      {loc.displayName || loc.name || 'Location'}
                    </div>
                    {isHq && <span style={styles.hqPill}>HQ</span>}
                  </div>
                  <div style={styles.locationAddress}>{addressText}</div>
                  {(phone || website) && (
                    <div style={styles.locationMeta}>
                      {phone && <span>{phone}</span>}
                      {website && (
                        <a
                          href={website.startsWith('http') ? website : `https://${website}`}
                          style={styles.locationLink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {formatWebsite(website)}
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div style={styles.locationActions}>
                  {canMark && (
                    <button
                      type="button"
                      style={styles.locationHqBtn}
                      onClick={() => onMarkHq?.(id)}
                    >
                      Mark as HQ
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function ImpactAreasCard({ areas }) {
  const list = Array.isArray(areas) ? areas : [];
  const countLabel =
    list.length === 0
      ? 'Add areas you serve during onboarding'
      : list.length === 1
      ? '1 area you serve'
      : `${list.length} areas you serve`;

  const groupedAreas = list
    .map((raw) => (raw || '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  return (
    <Card title="Areas Served" subtitle={countLabel} style={styles.areaCard}>
      {groupedAreas.length === 0 ? (
        <p style={styles.mutedText}>
          We’ll show the neighborhoods, cities, or regions you select during registration here.
        </p>
      ) : (
        <div style={styles.areasChipGrid}>
          {groupedAreas.map((area) => (
            <span key={area} style={styles.areaChipOval}>
              {area}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

function AuthorizedSignatoryCard({ signatory }) {
  const hasSig = !!signatory;
  const name =
    signatory?.name ||
    [signatory?.firstName, signatory?.lastName].filter(Boolean).join(' ');
  const role = signatory?.role || signatory?.title;
  const email = signatory?.email || signatory?.emailAddress;
  const acceptedAt = signatory?.acceptedAt ? new Date(signatory.acceptedAt) : null;
  const acceptedLabel =
    acceptedAt && !Number.isNaN(acceptedAt.getTime())
      ? acceptedAt.toLocaleDateString()
      : null;

  return (
    <Card
      title="Authorized signatory"
      subtitle="Person who can sign on behalf of your organization"
      style={{ ...styles.bandCard, ...styles.compactCard }}
    >
      {hasSig ? (
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{name}</div>
          {role && (
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              {role}
            </div>
          )}
          {email && (
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              {email}
            </div>
          )}
          {acceptedLabel && (
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
              Accepted on {acceptedLabel}
            </div>
          )}
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
            {renderRypilText('To change the signatory, contact your rypil representative.')}
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 13, opacity: 0.8 }}>
          We’ll show the signatory you provided during registration here once it’s available.
        </div>
      )}
    </Card>
  );
}

function ImpactMilestonesCard({ value, onSave }) {
  const [rows, setRows] = useState(() => normalizeMilestoneRows(value));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setRows(normalizeMilestoneRows(value));
  }, [value]);

  const updateRow = (segment, idx, patch) => {
    setRows((prev) => ({
      ...prev,
      [segment]: prev[segment].map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    }));
  };

  const addRow = (segment) => {
    setRows((prev) => ({
      ...prev,
      [segment]: [
        ...prev[segment],
        { id: randomRowId(segment), segment, amount: '', description: '' },
      ],
    }));
  };

  const removeRow = (segment, idx) => {
    setRows((prev) => {
      const current = prev[segment];
      if (current.length <= MIN_LEVELS_PER_SEGMENT) return prev;
      return {
        ...prev,
        [segment]: current.filter((_, i) => i !== idx),
      };
    });
  };

  const handleSave = async () => {
    setBusy(true);
    setMsg('');
    try {
      const payload = [...rows.individual, ...rows.community]
        .map((row) => ({
          segment: row.segment,
          thresholdCents: centsFromDollars(row.amount),
          description: (row.description || '').trim(),
        }))
        .filter((row) => row.description.length > 0);
      await onSave(payload);
      setMsg('Saved.');
    } catch (e) {
      setMsg(e?.message || 'Save failed.');
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(''), 2500);
    }
  };

  const renderSegment = (segment, title) => {
    const segmentRows = rows[segment] || [];
    const canRemove = segmentRows.length > MIN_LEVELS_PER_SEGMENT;
    return (
      <div key={segment} style={styles.milestoneSegment}>
        <div style={styles.segmentHeading}>{title}</div>
        {segmentRows.map((row, idx) => (
          <div key={row.id} style={styles.milestoneRow}>
            <div>
              <label style={styles.label}>Level {idx + 1} amount ($)</label>
              <input
                type="number"
                min={0}
                style={styles.input}
                value={row.amount}
                onChange={(e) => updateRow(segment, idx, { amount: e.target.value })}
              />
            </div>
            <div>
              <label style={styles.label}>Description</label>
              <input
                style={styles.input}
                placeholder="e.g., Plants 20 trees, funds 10 meals…"
                value={row.description}
                onChange={(e) => updateRow(segment, idx, { description: e.target.value })}
              />
            </div>
            <button
              type="button"
              style={{ ...styles.milestoneRemoveBtn, opacity: canRemove ? 1 : 0.35 }}
              onClick={() => removeRow(segment, idx)}
              disabled={!canRemove}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          style={styles.milestoneAddBtn}
          onClick={() => addRow(segment)}
        >
          + Add {segment === 'individual' ? 'individual' : 'community'} level
        </button>
      </div>
    );
  };

  return (
    <Card title="Impact milestones">
      <div style={styles.milestonesIntro}>
        <div style={styles.milestonesLead}>
          Show what different levels of support unlock for individuals and the community
        </div>
        <div style={styles.milestonesDetail}>
          Use simple, concrete statements so supporters see what their purchases add up
          to—individually and as a community.
        </div>
      </div>
      <div style={styles.milestonesRowOuter}>
        {renderSegment('individual', 'Individual milestones')}
        {renderSegment('community', 'Community milestones')}
      </div>
      <div style={styles.cardActions}>
        <button style={styles.primaryBtn} onClick={handleSave} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {!!msg && <span style={styles.messageText}>{msg}</span>}
      </div>
    </Card>
  );
}

/** ============== Styles ============== */

const styles = {
  fullscreen: {
    backgroundImage: `url(${tealBack})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    minHeight: '100vh',
    width: '100%',
    color: 'white',
    fontFamily: 'Helvetica, Arial, sans-serif',
  },
  dashboardMain: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    paddingBottom: 80,
  },
  dashboardInner: {
    width: 'min(1280px, 100%)',
    padding: '48px 24px 120px',
    display: 'grid',
    gap: 24,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    flexWrap: 'wrap',
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: 700,
    margin: 0,
  },
  headerSub: {
    fontSize: 14,
    opacity: 0.8,
  },
  headerRight: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  headerChips: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  logoutBtn: {
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.55)',
    background: 'rgba(15,23,42,0.55)',
    color: 'white',
    padding: '8px 18px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  manageProfileBtn: {
    borderRadius: 999,
    border: '1px solid rgba(148,163,184,0.7)',
    background: 'rgba(15,23,42,0.6)',
    color: 'white',
    padding: '8px 16px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 13,
  },
  errorCard: {
    background: 'rgba(248,113,113,0.12)',
    border: '1px solid rgba(248,113,113,0.45)',
    color: '#FCA5A5',
    padding: '10px 14px',
    borderRadius: 16,
  },
  loadingBlock: {
    display: 'grid',
    gap: 12,
    placeItems: 'center',
    padding: 32,
    borderRadius: 16,
    border: '1px solid rgba(148,163,184,0.35)',
    background: 'rgba(15,23,42,0.7)',
    backdropFilter: 'blur(16px)',
  },
  spinner: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '3px solid rgba(255,255,255,0.25)',
    borderTopColor: PEACH,
    animation: 'spin 1s linear infinite',
  },
  columns: {
    display: 'grid',
    gap: 24,
    gridTemplateColumns: 'minmax(320px, 340px) minmax(0, 1fr)',
    alignItems: 'flex-start',
  },
  bottomRow: {
    marginTop: 8,
  },
  bottomRowSplit: {
    marginTop: 8,
    display: 'grid',
    gap: 24,
    gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr)',
    alignItems: 'flex-start',
  },
  column: {
    display: 'grid',
    gap: 24,
    alignSelf: 'flex-start',
  },
  inlineCardsRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 0.9fr)',
    gap: 18,
  },
  card: {
    borderRadius: 22,
    border: '1px solid rgba(148,163,184,0.45)',
    background: 'linear-gradient(145deg, rgba(15,23,42,0.7), rgba(31,41,55,0.55))',
    padding: 18,
    display: 'grid',
    gap: 12,
    backdropFilter: 'blur(22px)',
    boxShadow: '0 18px 50px rgba(0,0,0,0.55)',
  },
  bandCard: {
    minHeight: 220, // shared height band so bottoms align
  },
  tallBandCard: {
    minHeight: 360,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  compactCard: {
    minHeight: 'auto',
    paddingTop: 12,
    paddingBottom: 12,
    gap: 8,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    flexWrap: 'wrap',
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  cardSubtitle: {
    fontSize: 13,
    opacity: 0.72,
    marginTop: 4,
  },
  primaryBtn: {
    background: PEACH,
    border: 'none',
    color: '#1F2933',
    borderRadius: 999,
    padding: '8px 16px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  secondaryBtn: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.3)',
    color: '#E5E7EB',
    borderRadius: 999,
    padding: '8px 14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  stripeCardActions: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  cardActionsRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 4,
  },
  bodyText: {
    fontSize: 13,
    opacity: 0.85,
  },
  mutedText: {
    fontSize: 13,
    opacity: 0.65,
    marginBottom: 4,
  },
  milestonesIntro: {
    display: 'grid',
    gap: 6,
    marginTop: 2,
    marginBottom: 6,
  },
  milestonesLead: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.4,
  },
  milestonesDetail: {
    fontSize: 13,
    opacity: 0.7,
    lineHeight: 1.45,
  },
  contributionsTop: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    alignItems: 'baseline',
    marginBottom: 8,
  },
  metricSlotLeft: {
    gridColumn: '1 / span 1',
  },
  metricSlotRight: {
    gridColumn: '3 / span 1',
  },
  metricLabel: {
    fontSize: 13,
    opacity: 0.75,
  },
  metricBig: {
    fontSize: 28,
    fontWeight: 700,
    marginTop: 4,
  },
  milestoneSegment: {
    display: 'grid',
    gap: 12,
  },
  segmentHeading: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    opacity: 0.8,
  },
  milestonesRowOuter: {
    display: 'grid',
    gap: 18,
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    alignItems: 'flex-start',
    marginTop: 8,
  },
  milestoneRow: {
    display: 'grid',
    gap: 12,
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    borderRadius: 14,
    border: '1px solid rgba(255,255,255,0.12)',
    padding: 12,
    background: 'rgba(15,23,42,0.7)',
    alignItems: 'end',
  },
  milestoneRemoveBtn: {
    border: '1px solid rgba(248,113,113,0.5)',
    background: 'transparent',
    color: '#FCA5A5',
    borderRadius: 999,
    padding: '6px 12px',
    height: 36,
    cursor: 'pointer',
  },
  milestoneAddBtn: {
    border: '1px dashed rgba(255,255,255,0.35)',
    background: 'transparent',
    color: 'white',
    borderRadius: 999,
    padding: '8px 14px',
    width: 'fit-content',
    cursor: 'pointer',
    fontSize: 13,
  },
  linkBtn: {
    background: 'transparent',
    border: 'none',
    color: '#93C5FD',
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
  },
  inputGrid: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  },
  label: {
    fontSize: 12,
    opacity: 0.75,
    display: 'block',
    marginBottom: 4,
  },
  holdNote: {
    fontSize: 11,
    opacity: 0.65,
    marginLeft: 6,
  },
  input: {
    width: '100%',
    borderRadius: 12,
    border: '1px solid rgba(148,163,184,0.6)',
    background: 'rgba(15,23,42,0.8)',
    color: 'white',
    padding: '8px 10px',
    boxSizing: 'border-box',
  },
  metricValue: {
    marginTop: 4,
    fontSize: 14,
    fontWeight: 600,
  },
  subLabel: {
    marginTop: 2,
    fontSize: 12,
    opacity: 0.6,
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 12,
  },
  messageText: {
    fontSize: 12,
    opacity: 0.7,
  },
  checklist: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'grid',
    gap: 8,
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 13,
  },
  checkDot: {
    width: 16,
    height: 16,
    borderRadius: '50%',
  },
  badge: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 48,
    opacity: 0.95,
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 999,
    border: '1px solid',
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 600,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  supportersRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 18,
  },
  supporterNumber: {
    fontSize: 32,
    fontWeight: 700,
  },
  supporterCopy: {
    fontSize: 13,
    opacity: 0.8,
  },
  locationList: {
    display: 'grid',
    gap: 10,
  },
  locationRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    padding: 12,
    borderRadius: 14,
    border: '1px solid rgba(148,163,184,0.5)',
    background: 'rgba(15,23,42,0.8)',
  },
  locationMain: {
    display: 'grid',
    gap: 4,
    minWidth: 0,
  },
  locationNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  locationName: {
    fontWeight: 600,
    fontSize: 14,
  },
  hqPill: {
    padding: '2px 8px',
    borderRadius: 999,
    border: '1px solid rgba(52,211,153,0.85)',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: '#6EE7B7',
  },
  locationAddress: {
    fontSize: 13,
    opacity: 0.8,
    whiteSpace: 'pre-line',
  },
  locationMeta: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    marginTop: 6,
    fontSize: 12,
    opacity: 0.75,
  },
  locationActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    alignItems: 'flex-end',
  },
  locationHqBtn: {
    borderRadius: 999,
    border: '1px solid rgba(148,163,184,0.9)',
    background: 'transparent',
    color: 'white',
    padding: '4px 10px',
    fontSize: 12,
    cursor: 'pointer',
  },
  locationLink: {
    color: '#93C5FD',
    textDecoration: 'none',
  },
  areaChipGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  areaChipOval: {
    padding: '6px 16px',
    borderRadius: 999,
    border: '1px solid rgba(148,163,184,0.7)',
    background: 'rgba(15,23,42,0.75)',
    fontSize: 13,
    whiteSpace: 'nowrap',
    letterSpacing: 0.2,
  },
  areaCard: {
    minHeight: 'auto',
    alignSelf: 'flex-start',
  },
};

if (typeof document !== 'undefined' && !document.getElementById('nfp-dashboard-spin')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'nfp-dashboard-spin';
  styleEl.innerHTML =
    '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
  document.head.appendChild(styleEl);
}
